/**
 * analyticsBeacon — the client half of the global-traffic pipeline
 * (docs/analytics-global-traffic-rollout.md, Option B).
 *
 * Queues lightweight analytics events and flushes them as small batches to
 * `POST /analytics/events` via `navigator.sendBeacon` (fetch keepalive as a
 * fallback), where the backend appends them to an NDJSON file for the rollup
 * aggregator. Never blocks navigation, never retries aggressively, and drops
 * wholesale when the "Track global traffic" site setting is off (which the
 * backend also enforces server-side).
 *
 * Gating reads the SiteSettingsContext localStorage cache directly so this
 * module stays framework-free and costs no extra requests.
 */

import { API_BASE_URL } from "@/src/services/api";
const SETTINGS_CACHE_KEY = "pc_site_settings_v1";
const BROWSER_ID_KEY = "analytics_tracker.browserId";
const FLUSH_MS = 15_000;
const FLUSH_AT = 20; // batch size that triggers an immediate flush
const MAX_QUEUE = 200; // hard cap — drop oldest beyond this
const SETTINGS_TTL_MS = 60_000;
const isBrowser = typeof window !== "undefined";
let queue = [];
let flushTimer = null;
let cachedState = null;
let cachedStateAt = 0;
let listenersBound = false;

/**
 * Read the tracking flag from the settings localStorage cache (60s memo).
 * Tri-state: "unknown" (cache absent — e.g. the very first visit before the
 * settings fetch lands) is deliberately NEVER memoized, so first-session
 * top-of-funnel events buffer instead of being dropped for a full minute.
 */
function trackingState() {
  if (!isBrowser) return "off";
  const now = Date.now();
  if (cachedState !== null && now - cachedStateAt < SETTINGS_TTL_MS) {
    return cachedState;
  }
  let raw = null;
  try {
    raw = window.localStorage.getItem(SETTINGS_CACHE_KEY);
  } catch {
    return "off";
  }
  if (!raw) return "unknown"; // not memoized — re-check on next event/flush
  let state = "off";
  try {
    const parsed = JSON.parse(raw);
    state = parsed?.trackGlobalTraffic === true ? "on" : "off";
  } catch {
    state = "off";
  }
  cachedState = state;
  cachedStateAt = now;
  return state;
}
function browserId() {
  if (!isBrowser) return "";
  try {
    const raw = window.localStorage.getItem(BROWSER_ID_KEY) ?? "";
    // The aggregates store writes the id JSON-encoded (quoted) — accept
    // both forms so identities line up across writers.
    if (raw.startsWith('"')) {
      try {
        return String(JSON.parse(raw));
      } catch {
        return raw;
      }
    }
    return raw;
  } catch {
    return "";
  }
}
function bindLifecycleListeners() {
  if (!isBrowser || listenersBound) return;
  listenersBound = true;
  // Flush whatever is queued when the page is being backgrounded/closed —
  // sendBeacon is designed exactly for this.
  window.addEventListener("pagehide", () => flushNow());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushNow();
  });
}
function flushNow() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!queue.length) return;
  const state = trackingState();
  if (state === "off") {
    queue = [];
    return;
  }
  if (state === "unknown") {
    // Settings not cached yet (first visit) — hold the buffered events
    // and try again on the next timer tick instead of dropping them.
    scheduleFlush();
    return;
  }
  const events = queue;
  queue = [];
  const payload = JSON.stringify({
    events
  });
  const url = `${API_BASE_URL}/analytics/events`;
  // sendBeacon can THROW in some browsers for cross-origin Blobs (not just
  // return false), so the fetch fallback must also cover the throw path —
  // otherwise the batch is silently lost in exactly the browsers the
  // fallback exists for.
  let sent = false;
  try {
    const blob = new Blob([payload], {
      type: "application/json"
    });
    sent = typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function" && navigator.sendBeacon(url, blob);
  } catch {
    sent = false;
  }
  if (!sent) {
    try {
      void fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: payload,
        keepalive: true
      }).catch(() => undefined);
    } catch {
      // Analytics must never surface an error to the app.
    }
  }
}
function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushNow();
  }, FLUSH_MS);
}

/** Omit that distributes over each member of a union type. */

/**
 * Queue one event for the global pipeline. No-ops (and empties the queue)
 * when the "Track global traffic" site setting is off.
 */
export function enqueueGlobalAnalyticsEvent(event) {
  if (!isBrowser) return;
  const state = trackingState();
  if (state === "off") {
    queue = [];
    return;
  }
  // "unknown" (settings not fetched yet): buffer the event — flushNow holds
  // it until the flag is known, so first-visit events aren't dropped.
  const id = browserId();
  if (!id) return; // aggregates store not initialized yet
  bindLifecycleListeners();
  queue.push({
    ...event,
    ts: event.ts ?? Date.now(),
    browserId: id
  });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
  if (state === "on" && queue.length >= FLUSH_AT) flushNow();else scheduleFlush();
}

/** Queue a user-action event (save_item, inquiry_submitted, …). */
export function trackAnalyticsAction(name) {
  enqueueGlobalAnalyticsEvent({
    kind: "action",
    name
  });
}

// ── Automatic capture: session source, gate funnel, scroll depth, vitals ──

const SESSION_SENT_KEY = "analytics_tracker.sessionSent";
const GATE_SENT_KEY = "analytics_tracker.gateSent";

/** Derive the analytics section from a pathname. */
function sectionOf(pathname) {
  const m = pathname.match(/^\/(organizations|events|regulations|articles|professionals)(\/|$)/);
  if (m) return m[1].replace(/s$/, "");
  if (pathname === "/" || pathname.startsWith("/home")) return "home";
  return "other";
}
let captureBound = false;
let maxDepth = 0;
let currentSection = "other";
let vitalsSent = false;
let clsValue = 0;
let lcpValue = 0;
let inpValue = 0;
function emitScrollDepth() {
  if (maxDepth > 0) {
    enqueueGlobalAnalyticsEvent({
      kind: "scroll",
      name: currentSection,
      depth: Math.round(maxDepth)
    });
    maxDepth = 0;
  }
}
function emitVitalsOnce() {
  if (vitalsSent) return;
  vitalsSent = true;
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav && nav.responseStart > 0) {
      enqueueGlobalAnalyticsEvent({
        kind: "vital",
        name: "ttfb",
        value: Math.round(nav.responseStart)
      });
    }
  } catch {
    // navigation timing unavailable
  }
  if (lcpValue > 0) enqueueGlobalAnalyticsEvent({
    kind: "vital",
    name: "lcp",
    value: Math.round(lcpValue)
  });
  if (clsValue > 0) enqueueGlobalAnalyticsEvent({
    kind: "vital",
    name: "cls",
    value: Math.round(clsValue * 1000) / 1000
  });
  if (inpValue > 0) enqueueGlobalAnalyticsEvent({
    kind: "vital",
    name: "inp",
    value: Math.round(inpValue)
  });
}

/**
 * Notify the beacon of an SPA route change: flushes the previous page's
 * scroll depth and rebases the tracker on the new section.
 */
export function noteAnalyticsRouteChange(pathname) {
  if (!isBrowser) return;
  emitScrollDepth();
  currentSection = sectionOf(pathname);
}

/**
 * Bind the passive global capture (idempotent; call once from _app):
 *  - "session" event once per browser session with the referrer/UTM source;
 *  - "gate_passed" funnel step once per session after the splash gate;
 *  - max scroll depth per page, emitted on route change / page hide;
 *  - Core Web Vitals (LCP, CLS, INP via PerformanceObserver; TTFB from
 *    navigation timing), emitted once when the page is first hidden.
 * All events flow through enqueueGlobalAnalyticsEvent, so everything drops
 * wholesale when the "Track global traffic" setting is off.
 */
export function initGlobalAnalyticsCapture() {
  if (!isBrowser || captureBound) return;
  captureBound = true;
  currentSection = sectionOf(window.location.pathname);

  // Session source (once per tab session).
  try {
    if (!sessionStorage.getItem(SESSION_SENT_KEY)) {
      sessionStorage.setItem(SESSION_SENT_KEY, "1");
      let source = "direct";
      const params = new URLSearchParams(window.location.search);
      const utm = params.get("utm_source");
      if (utm) {
        source = `utm:${utm.slice(0, 100)}`;
      } else if (document.referrer) {
        try {
          const ref = new URL(document.referrer);
          if (ref.host !== window.location.host) source = ref.host.slice(0, 120);
        } catch {
          // unparsable referrer — keep "direct"
        }
      }
      enqueueGlobalAnalyticsEvent({
        kind: "session",
        source
      });
    }
  } catch {
    // sessionStorage unavailable
  }

  // Gate-passed funnel step (once per tab session, once inside the app).
  try {
    if (!sessionStorage.getItem(GATE_SENT_KEY) && document.cookie.includes("gate=ok")) {
      sessionStorage.setItem(GATE_SENT_KEY, "1");
      enqueueGlobalAnalyticsEvent({
        kind: "auth_step",
        name: "gate_passed"
      });
    }
  } catch {
    // sessionStorage unavailable
  }

  // Scroll depth.
  window.addEventListener("scroll", () => {
    const doc = document.documentElement;
    const total = doc.scrollHeight - window.innerHeight;
    if (total <= 0) return;
    const depth = Math.min(100, (window.scrollY || doc.scrollTop) / total * 100);
    if (depth > maxDepth) maxDepth = depth;
  }, {
    passive: true
  });
  window.addEventListener("pagehide", () => {
    emitScrollDepth();
    emitVitalsOnce();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") emitVitalsOnce();
  });

  // Core Web Vitals observers (best-effort; unsupported types throw).
  try {
    new PerformanceObserver(list => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last?.startTime) lcpValue = last.startTime;
    }).observe({
      type: "largest-contentful-paint",
      buffered: true
    });
  } catch {
    // LCP unsupported
  }
  try {
    new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput && entry.value) clsValue += entry.value;
      }
    }).observe({
      type: "layout-shift",
      buffered: true
    });
  } catch {
    // CLS unsupported
  }
  try {
    new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (entry.interactionId && (entry.duration ?? 0) > inpValue) inpValue = entry.duration ?? 0;
      }
    }).observe({
      type: "event",
      buffered: true,
      // @ts-expect-error durationThreshold is a valid event-timing opt
      durationThreshold: 40
    });
  } catch {
    // INP unsupported
  }
}